import React, { useState, useEffect } from 'react';
import { Building2, CheckCircle, Calendar, Users, Award, TrendingUp } from 'lucide-react';
import EventCard from '../components/features/EventCard';
import { eventsService } from '../services/api-service';
import './VenuePortfolio.css';

const VenuePortfolio = () => {
  const [pastEvents, setPastEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const [past, upcoming] = await Promise.all([
        eventsService.getPastEvents(),
        eventsService.getUpcomingEvents()
      ]);
      setPastEvents(past);
      setUpcomingEvents(upcoming);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="venue-portfolio-page">
      {/* Header */}
      <section className="venue-header">
        <div className="container">
          <div className="venue-header-content">
            <Building2 size={48} className="venue-header-icon" />
            <h1>Venue Partnership Portfolio</h1>
            <p className="venue-tagline">
              Professional Event Hosting for Conscious Community Gatherings
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="venue-mission section">
        <div className="container">
          <div className="venue-mission-content">
            <h2>About Back to People</h2>
            <p className="venue-lead">
              Creating spaces where people come together authentically to celebrate life, 
              expand consciousness, and build meaningful relationships through cultural, 
              spiritual, and seasonal celebrations.
            </p>
            <div className="venue-hosts">
              <div className="venue-host">
                <h3>Rama</h3>
                <p>Event Producer, Audio Rhythm Creator, Community Builder</p>
              </div>
              <div className="venue-host">
                <h3>Zee</h3>
                <p>Event Producer, Visual Rhythm Creator, Community Builder</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Track Record */}
      <section className="venue-track-record section bg-gray">
        <div className="container">
          <h2>Proven Track Record</h2>
          <div className="venue-achievement">
            <div className="venue-achievement-header">
              <Award size={32} />
              <div>
                <h3>October 18th, 2025 - Back2People Coolum Beach</h3>
                <p className="venue-achievement-theme">Leave No Trace Environmental Celebration</p>
              </div>
            </div>
            <div className="venue-results">
              <div className="venue-result-item">
                <CheckCircle size={24} />
                <div>
                  <h4>Zero Waste</h4>
                  <p>Complete "Leave No Trace" success</p>
                </div>
              </div>
              <div className="venue-result-item">
                <CheckCircle size={24} />
                <div>
                  <h4>Professional Artists</h4>
                  <p>Monzin, Facundo Prato, Felipe Supertino</p>
                </div>
              </div>
              <div className="venue-result-item">
                <CheckCircle size={24} />
                <div>
                  <h4>Community Respect</h4>
                  <p>All attendees followed guidelines</p>
                </div>
              </div>
              <div className="venue-result-item">
                <CheckCircle size={24} />
                <div>
                  <h4>Venue Preservation</h4>
                  <p>Location maintained in pristine condition</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="venue-offer section">
        <div className="container">
          <h2>What We Bring to Your Venue</h2>
          <div className="venue-offer-grid">
            <div className="venue-offer-item">
              <Users size={40} />
              <h3>Quality Community</h3>
              <p>
                Vetted attendees who respect venues and follow all guidelines. We only 
                invite people who have proven their commitment to mutual respect.
              </p>
            </div>
            <div className="venue-offer-item">
              <CheckCircle size={40} />
              <h3>Zero Liability Risk</h3>
              <p>
                Complete cleanup, Leave No Trace principles, and responsible attendees 
                mean zero venue damage and no complaints.
              </p>
            </div>
            <div className="venue-offer-item">
              <TrendingUp size={40} />
              <h3>Professional Production</h3>
              <p>
                AI-generated promotional content, professional artists, and high-quality 
                event production that elevates your venue's reputation.
              </p>
            </div>
            <div className="venue-offer-item">
              <Calendar size={40} />
              <h3>Flexible Scheduling</h3>
              <p>
                Multiple available dates throughout the year for seasonal celebrations, 
                cultural events, and themed gatherings.
              </p>
            </div>
            <div className="venue-offer-item">
              <Award size={40} />
              <h3>Cultural Respect</h3>
              <p>
                Events honoring cultural traditions, seasonal cycles, and spiritual 
                significance - creating meaningful experiences.
              </p>
            </div>
            <div className="venue-offer-item">
              <Building2 size={40} />
              <h3>Long-term Partnership</h3>
              <p>
                Build ongoing relationships with recurring events, expanding your venue's 
                reach in the conscious community market.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="venue-event-types section bg-black text-white">
        <div className="container">
          <h2>Event Types</h2>
          <div className="venue-event-types-grid">
            <div className="venue-event-type">
              <h3>Seasonal Celebrations</h3>
              <ul>
                <li>Summer/Winter Solstice</li>
                <li>Spring/Autumn Equinox</li>
                <li>Full Moon Ceremonies</li>
              </ul>
            </div>
            <div className="venue-event-type">
              <h3>Cultural & Spiritual</h3>
              <ul>
                <li>Halloween & Day of the Dead</li>
                <li>Easter Transformation</li>
                <li>Beltane Fire Events</li>
              </ul>
            </div>
            <div className="venue-event-type">
              <h3>Themed Gatherings</h3>
              <ul>
                <li>New Year Intentions</li>
                <li>Valentine's Connection</li>
                <li>Christmas Celebrations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <section className="venue-past-events section">
          <div className="container">
            <h2>Past Events Showcase</h2>
            <div className="venue-events-grid">
              {pastEvents.map((event) => (
                <EventCard key={event.id} event={event} showArtists={true} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="venue-upcoming section bg-gray">
          <div className="container">
            <h2>Upcoming Events</h2>
            <p className="venue-upcoming-lead">
              Available dates for venue partnerships in 2025-2026
            </p>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <p>Loading events...</p>
              </div>
            ) : (
              <div className="venue-events-grid">
                {upcomingEvents.slice(0, 6).map((event) => (
                  <EventCard key={event.id} event={event} showArtists={false} compact={true} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="venue-cta section bg-black text-white">
        <div className="container">
          <div className="venue-cta-content">
            <h2>Partner With Us</h2>
            <p>
              Interested in hosting a Back to People event at your venue? Let's create 
              something special together.
            </p>
            <div className="venue-cta-buttons">
              <a href="/contact" className="btn btn-white">
                Get in Touch
              </a>
              <a href="/about" className="btn btn-outline btn-white">
                Learn More About B2P
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="venue-philosophy section">
        <div className="container">
          <blockquote className="venue-quote">
            "If we can't respect the earth, how can we respect each other?"
          </blockquote>
          <p className="venue-philosophy-text">
            Community-focused gatherings that honor cultural traditions, respect the 
            environment, and create lasting connections. Quality over quantity, every time.
          </p>
        </div>
      </section>
    </div>
  );
};

export default VenuePortfolio;

